import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, Star, Users, GraduationCap } from 'lucide-react';
import api from '../../services/api';

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('all');
  const [difficulty, setDifficulty] = useState('all');

  useEffect(() => {
    Promise.all([api.get('/catalog'), api.get('/categories')])
      .then(([c, cat]) => {
        setCourses(c.data?.data || c.data || []);
        setCategories(cat.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = courses.filter(c => {
    const matchSearch = !search || c.title?.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCat === 'all' || c.category_id == selectedCat;
    const matchDiff = difficulty === 'all' || c.difficulty_level === difficulty;
    return matchSearch && matchCat && matchDiff;
  });

  const diffLabels = { beginner: "Boshlang'ich", intermediate: "O'rta", advanced: "Yuqori" };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2"><BookOpen className="w-8 h-8" /> Kurslar katalogi</h1>
        <p className="text-blue-100 mb-4">O'zingizga mos kursni toping</p>
        <div className="relative max-w-xl">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Kurs nomi bo'yicha qidiring..." className="w-full px-5 py-3 rounded-xl bg-white/20 border border-white/20 text-white placeholder-blue-200 outline-none focus:border-white/40" />
          <Search className="absolute right-4 top-3.5 w-5 h-5 text-blue-200" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:border-blue-500 outline-none">
          <option value="all">Barcha kategoriyalar</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm bg-white focus:border-blue-500 outline-none">
          <option value="all">Barcha darajalar</option>
          {Object.entries(diffLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="px-4 py-2 text-sm text-gray-500">{filtered.length} ta kurs</span>
      </div>

      {/* Course Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(c => (
          <Link key={c.id} to={`/courses/${c.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all group">
            <div className="h-40 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center relative">
              {c.image_url ? <img src={c.image_url} className="w-full h-full object-cover" /> : <BookOpen className="w-16 h-16 text-gray-300 opacity-50" />}
              {c.difficulty_level && (
                <span className={`absolute top-3 right-3 text-xs px-2.5 py-1 rounded-full font-medium ${c.difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' : c.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                  {diffLabels[c.difficulty_level]}
                </span>
              )}
            </div>
            <div className="p-5">
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition">{c.title}</h3>
              <p className="text-sm text-gray-500 line-clamp-2 mb-3">{c.description}</p>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {c.teacher?.name}</span>
                <div className="flex items-center gap-3">
                  {c.rating > 0 && <span className="text-yellow-500 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {c.rating}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.students_count || 0}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <div className="mb-4 flex justify-center"><Search className="w-12 h-12 text-gray-300" /></div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Kurs topilmadi</h3>
          <p className="text-gray-500">Qidiruv so'rovini o'zgartiring</p>
        </div>
      )}
    </div>
  );
}